import { create } from 'zustand'
import { fetchAuthSession } from 'aws-amplify/auth'

const WS_URL = process.env.REACT_APP_WS_URL
  ? process.env.REACT_APP_WS_URL.replace(/^"|"$/g, '')
  : 'wss://3lxb30ffie.execute-api.us-west-2.amazonaws.com/prod'

type MessageRole = 'user' | 'assistant'

interface Message {
  role: MessageRole
  content: string
  timestamp?: number
}

interface ChatState {
  messages: Message[]
  isLoading: boolean
  error: string | null
  sessionId: string | null
  ws: WebSocket | null
  statusText: string | null
  statusAgent: string | null
  sendMessage: (content: string) => Promise<void>
  clearMessages: () => void
  setMessages: (messages: Message[]) => void
}

function generateSessionId(): string {
  return crypto.randomUUID()
}

function getOrCreateSessionId(): string {
  const stored = localStorage.getItem('askED_sessionId')
  if (stored) return stored
  const id = generateSessionId()
  localStorage.setItem('askED_sessionId', id)
  return id
}

async function getWebSocket(): Promise<{ ws: WebSocket; token: string }> {
  const { tokens } = await fetchAuthSession()
  const idToken = tokens?.idToken?.toString()
  if (!idToken) throw new Error('No authentication token')

  return new Promise((resolve, reject) => {
    const ws = new WebSocket(`${WS_URL}?token=${idToken}`)
    ws.onopen = () => resolve({ ws, token: idToken })
    ws.onerror = () => reject(new Error('WebSocket connection failed'))
    setTimeout(() => reject(new Error('WebSocket connection timeout')), 10000)
  })
}

export const useChatStore = create<ChatState>((set, get) => ({
  messages: [],
  isLoading: false,
  error: null,
  sessionId: null,
  ws: null,
  statusText: null,
  statusAgent: null,

  sendMessage: async (content: string) => {
    const userMessage: Message = { role: 'user', content, timestamp: Date.now() }

    let sessionId = get().sessionId
    if (!sessionId) {
      sessionId = getOrCreateSessionId()
    }

    set((state) => ({
      messages: [...state.messages, userMessage],
      isLoading: true,
      error: null,
      sessionId,
    }))

    try {
      const { ws, token } = await getWebSocket()
      let assistantContent = ''

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data)

        if (data.type === 'status') {
          set({ statusText: data.content, statusAgent: data.agent || null })
        }

        if (data.type === 'chunk' && data.content) {
          set({ statusText: null, statusAgent: null })
          assistantContent += data.content
          // Update message in real-time
          set((state) => {
            const msgs = [...state.messages]
            const lastMsg = msgs[msgs.length - 1]
            if (lastMsg && lastMsg.role === 'assistant') {
              lastMsg.content = assistantContent
            } else {
              msgs.push({ role: 'assistant', content: assistantContent, timestamp: Date.now() })
            }
            return { messages: msgs }
          })
        }

        if (data.type === 'done') {
          const finalContent = assistantContent || "Sorry, I couldn't generate a response."
          set((state) => {
            const msgs = [...state.messages]
            const lastMsg = msgs[msgs.length - 1]
            if (lastMsg && lastMsg.role === 'assistant') {
              lastMsg.content = finalContent
            } else {
              msgs.push({ role: 'assistant', content: finalContent, timestamp: Date.now() })
            }
            return { messages: msgs, isLoading: false, statusText: null, statusAgent: null }
          })
          ws.close()
        }

        if (data.type === 'error') {
          const errorMsg = 'Sorry, something went wrong. Please try again or start a new conversation.'
          set((state) => {
            const msgs = [...state.messages]
            const lastMsg = msgs[msgs.length - 1]
            if (lastMsg && lastMsg.role === 'assistant') {
              lastMsg.content = errorMsg
            } else {
              msgs.push({ role: 'assistant', content: errorMsg, timestamp: Date.now() })
            }
            return { messages: msgs, isLoading: false, error: null, statusText: null, statusAgent: null }
          })
          ws.close()
        }
      }

      ws.onerror = () => {
        set({ isLoading: false, error: 'Connection error. Please try again.' })
      }

      ws.onclose = () => {
        // Ensure loading stops if connection closes unexpectedly
        if (get().isLoading) {
          const finalContent = assistantContent || "Sorry, the connection was lost."
          set((state) => {
            const msgs = [...state.messages]
            const lastMsg = msgs[msgs.length - 1]
            if (lastMsg && lastMsg.role === 'assistant') {
              lastMsg.content = finalContent
            } else if (assistantContent) {
              msgs.push({ role: 'assistant', content: finalContent, timestamp: Date.now() })
            }
            return { messages: msgs, isLoading: false }
          })
        }
      }

      // Send the message
      ws.send(JSON.stringify({
        action: 'sendMessage',
        prompt: content,
        sessionId,
        token,
      }))

    } catch (error) {
      console.error('Error sending message:', error)
      set({ isLoading: false, error: 'Failed to send message. Please try again.' })
    }
  },

  clearMessages: () => {
    const newSessionId = generateSessionId()
    localStorage.setItem('askED_sessionId', newSessionId)
    set({ messages: [], error: null, sessionId: newSessionId, statusText: null, statusAgent: null })
  },

  setMessages: (messages: Message[]) => {
    set({ messages })
  },
}))
